// Header.jsx
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Fade from '@mui/material/Fade';

// project imports
import LogoSection from '../LogoSection';
import SearchSection from './SearchSection';
import ProfileSection from './ProfileSection';
import NotificationSection from './NotificationSection';
import { RestrictedToRole } from '../../../roles/RestrictedToRole';
import useConfig from '../../../hooks/useConfig';

// icons
import {
  IconMenu2,
  IconSun,
  IconMoon,
  IconArrowLeft,
  IconTerminal2
} from '@tabler/icons-react';
import InProgress from '../../../assets/images/icons/InProgress.gif';
import HR from '../../../assets/images/icons/hr.png';

// react
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// api
import { handlerDrawerOpen, useGetMenuMaster } from '../../../api/menu';
import { hasIncompleteReviews } from '../../../services/probationReviewAPIs';

export default function Header() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { mode, onChangeMode } = useConfig();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const [notification, setNotification] = useState(true);
  const [hasPendingProbation, setHasPendingProbation] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const checkIncompleteProbation = async () => {
      try {
        const userRole = localStorage.getItem('userRole');
        const allowedRoles = ['DIRECTOR', 'MANAGER', 'HR', 'DEV'];
        if (allowedRoles.includes(userRole)) {
          const res = await hasIncompleteReviews();
          if (isMounted) {
            setHasPendingProbation(Boolean(res));
          }
        }
      } catch (err) {
        console.error('Error fetching incomplete probation reviews status:', err);
      }
    };

    checkIncompleteProbation();
    return () => {
      isMounted = false;
    };
  }, []);

  // Toggle dark mode using ConfigContext
  const handleToggleDarkMode = () => {
    onChangeMode(mode === 'light' ? 'dark' : 'light');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDrawerToggle = () => {
    handlerDrawerOpen(!drawerOpen);
  };

  // Reusable sleek icon button style
  const premiumAvatarStyle = {
    ...theme.typography.commonAvatar,
    ...theme.typography.mediumAvatar,
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all .3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    color: mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[800],
    border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
    boxShadow: mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.03)',
    '&:hover': {
      bgcolor: theme.palette.primary.main,
      color: '#fff',
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 15px ${theme.palette.primary.main}40`
    },
  };

  return (
    <>
      <Box
        sx={{
          width: downMD ? 'auto' : 228,
          display: 'flex',
          margin: 0,
          alignItems: 'center',
          justifyContent: drawerOpen ? 'space-between' : 'flex-start',
        }}
      >
        {drawerOpen && (
          <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 10 }}>
            <Box component="span" sx={{ alignSelf: 'center', display: { xs: 'none', md: 'flex' }, alignItems: 'flex-end', flexGrow: 10, minWidth: 120 }}>
              <LogoSection />
            </Box>
          </Box>
        )}

        <Avatar
          variant="rounded"
          sx={{
            ...premiumAvatarStyle,
            ...(drawerOpen && { ml: 'auto' }), // Push to right when drawer open
          }}
          onClick={handleDrawerToggle}
          color="inherit"
        >
          <IconMenu2 stroke={1.5} size="20px" />
        </Avatar>
      </Box>

      {/* SEARCH BAR */}
      {drawerOpen && <SearchSection />}

      <Box sx={{ flexGrow: 1 }} />

      {/* HEADER RIGHT ACTIONS */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

        {/* Go Back */}
        <Tooltip title="Go Back" TransitionComponent={Fade}>
          <IconButton
            onClick={handleGoBack}
            aria-label="Go back"
            sx={{
              ...premiumAvatarStyle,
              width: 34,
              height: 34, // match other buttons usually
            }}
          >
            <IconArrowLeft size={18} />
          </IconButton>
        </Tooltip>

        {/* Progress Tracker */}
        <RestrictedToRole allowedRoles={['DIRECTOR', 'MANAGER', 'HR', 'DEV']}>
          {hasPendingProbation && (
            <Box
              onClick={() => navigate('/reports/probation-reviews')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                height: 34,
                borderRadius: '17px',
                px: '11px',
                bgcolor: mode === 'dark' ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.08)',
                border: `1px solid ${mode === 'dark' ? 'rgba(244, 67, 54, 0.4)' : 'rgba(244, 67, 54, 0.3)'}`,
                boxShadow: mode === 'dark' ? '0 2px 10px rgba(244, 67, 54, 0.2)' : '0 2px 8px rgba(244, 67, 54, 0.12)',
                transition: 'all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                overflow: 'hidden',
                maxWidth: '34px',
                boxSizing: 'border-box',
                '&:hover': {
                  maxWidth: '250px',
                  bgcolor: mode === 'dark' ? 'rgba(244, 67, 54, 0.22)' : 'rgba(244, 67, 54, 0.14)',
                  borderColor: 'rgba(244, 67, 54, 0.65)',
                  boxShadow: '0 4px 16px rgba(244, 67, 54, 0.35)',
                  transform: 'translateY(-1px)',
                  '& .review-badge-text': {
                    opacity: 1,
                    maxWidth: '200px',
                    ml: 1,
                    mr: 0.5,
                  },
                },
                '@keyframes redDotFlicker': {
                  '0%, 100%': {
                    opacity: 1,
                    transform: 'scale(1)',
                    boxShadow: '0 0 6px 2px rgba(244, 67, 54, 0.9), 0 0 12px 4px rgba(244, 67, 54, 0.4)',
                  },
                  '30%': {
                    opacity: 0.45,
                    transform: 'scale(0.85)',
                    boxShadow: '0 0 2px 1px rgba(244, 67, 54, 0.3)',
                  },
                  '60%': {
                    opacity: 1,
                    transform: 'scale(1.18)',
                    boxShadow: '0 0 9px 3px rgba(244, 67, 54, 1), 0 0 16px 6px rgba(244, 67, 54, 0.5)',
                  },
                },
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  minWidth: 10,
                  minHeight: 10,
                  borderRadius: '50%',
                  bgcolor: '#f44336',
                  animation: 'redDotFlicker 1.6s infinite ease-in-out',
                  flexShrink: 0,
                }}
              />
              <Box
                className="review-badge-text"
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.3px',
                  whiteSpace: 'nowrap',
                  color: mode === 'dark' ? '#ff8a80' : '#d32f2f',
                  opacity: 0,
                  maxWidth: 0,
                  transition: 'all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  userSelect: 'none',
                }}
              >
                Pending probation reviews
              </Box>
            </Box>
          )}
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Progress Tracking" TransitionComponent={Fade}>
              <Avatar
                variant="rounded"
                sx={premiumAvatarStyle}
                onClick={() => navigate('/progress-tracking')}
              >
                <img src={InProgress} alt={'Progress'} width={'26px'} height={'26px'} style={{ borderRadius: '50%' }} />
              </Avatar>
            </Tooltip>
          </Box> */}
        </RestrictedToRole>

        {/* IT Center */}
        {/* <Tooltip title="IT Center" TransitionComponent={Fade}>
          <Avatar
            variant="rounded"
            sx={premiumAvatarStyle}
            onClick={() => navigate('/it')}
          >
            <IconTerminal2 stroke={1.5} size="20px" />
          </Avatar>
        </Tooltip> */}

        {/* Dark Mode */}
        <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'} TransitionComponent={Fade}>
          <Avatar
            variant="rounded"
            sx={premiumAvatarStyle}
            onClick={handleToggleDarkMode}
          >
            {mode === 'dark' ? <IconSun stroke={1.5} size="20px" /> : <IconMoon stroke={1.5} size="20px" />}
          </Avatar>
        </Tooltip>

        {/* HR Absences */}
        {/* <RestrictedToRole allowedRoles={['HR', 'DEV']}>
          <Tooltip title="HR Absences" TransitionComponent={Fade}>
            <Avatar
              variant="rounded"
              sx={premiumAvatarStyle}
              onClick={() => navigate('/absence')}
            >
              <img src={HR} alt={'HR'} width={'22px'} height={'22px'} style={{ filter: mode === 'dark' ? 'invert(1)' : 'none' }} />
            </Avatar>
          </Tooltip>
        </RestrictedToRole> */}

        {/* Notifications */}
        <NotificationSection allow={notification} />

        {/* DEV MODE Indicator */}
        {localStorage.getItem('userRole') === 'DEV' && (
          <Tooltip title="Development Mode Active" TransitionComponent={Fade}>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                background: 'linear-gradient(135deg, #111111 0%, #2a2a2a 100%)',
                color: '#00ffcc',
                border: '1px solid rgba(0, 255, 204, 0.3)',
                boxShadow: '0 0 10px rgba(0, 255, 204, 0.15)',
                borderRadius: '8px',
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: 1.2,
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                userSelect: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 0 15px rgba(0, 255, 204, 0.3)',
                  transform: 'translateY(-1px)',
                }
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00ffcc', boxShadow: '0 0 8px #00ffcc', animation: 'eid-twinkle 1.5s infinite' }} />
              DEV MODE
            </Box>
          </Tooltip>
        )}

        {/* Profile */}
        <Box sx={{ ml: 1, pl: 1, borderLeft: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
          <ProfileSection notification={notification} setNotification={setNotification} />
        </Box>

      </Box>
    </>
  );
}
